// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { FC, PropsWithChildren, useContext } from "react";
import { css } from "styled-components";
// XNG-Changes
const enum fields {
    showImage = "showImage",
    showLinks = "showLinks",
}

interface Props extends WidgetProps {
    // XNG-Changes
    fields: {
        [fields.showImage]: boolean;
        [fields.showLinks]: boolean;
    };
}

export interface BrandDetailsImageStyles {
    container?: GridContainerProps;
    imageItem?: GridItemProps;
    image?: LazyImageProps;
    imageMissingText?: TypographyPresentationProps;
    linksItem?: GridItemProps;
    linksContainer?: GridContainerProps;
    productLinkItem?: GridItemProps;
    productLink?: LinkPresentationProps;
    externalLinkItem?: GridItemProps;
    externalLink?: LinkPresentationProps;
    // XNG-Changes
    showAllProductsButton?: ButtonPresentationProps;
}

export const imageStyles: BrandDetailsImageStyles = {
    container: {
        gap: 5,
        css: css`
            margin: 15px;
        `,
    },
    imageItem: {
        width: 12,
    },
    linksItem: {
        width: 12,
    },
    linksContainer: {
        gap: 20,
        css: css`
            padding-top: 15px;
        `,
    },
    image: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    imageMissingText: {
        variant: "h3",
        css: css`
            width: 100%;
            overflow-wrap: break-word;
            word-wrap: break-word;
        `,
    },
    productLinkItem: {
        width: 12,
        css: css`
            justify-content: center;
            text-align: center;
        `,
    },
    externalLinkItem: {
        width: 12,
        css: css`
            justify-content: center;
            text-align: center;
        `,
    },
};

const styles = imageStyles;

const BrandDetailsImage: FC<Props> = ({ fields: { showImage, showLinks } }) => {
    const { value: brand } = useContext(BrandStateContext);
    if (!brand) {
        return null;
    }

    return (
        // XNG-Changes : change structure
        <GridContainer data-test-selector="brandImage" {...styles.container}>
            {showImage && brand.featuredImagePath && (
                <GridItem {...styles.imageItem}>
                    {/* {brand.featuredImagePath ? ( */}
                    <LazyImage
                        data-test-selector="brandFeaturedImage"
                        altText={brand.featuredImageAltText}
                        src={brand.featuredImagePath}
                        {...styles.image}
                    />
                    {/* ) : (
                        <Typography data-test-selector="brandFeaturedImageMissingText" {...styles.imageMissingText}>
                            {brand.name}
                        </Typography>
                    )} */}
                </GridItem>
            )}
            {showLinks && (
                <GridItem {...styles.linksItem}>
                    {/* <GridContainer {...styles.linksContainer}> */}
                    <StyledWrapper {...styles.linksContainer}>
                        {/* <GridItem {...styles.productLinkItem}> */}
                        <Button
                            data-test-selector="brandToProductPageLink"
                            onClick={() => {
                                window.location.href = brand.productListPagePath;
                            }}
                            {...styles.showAllProductsButton}
                        >
                            {translate("Shop All {0} Products").replace("{0}", brand.name)}
                        </Button>
                        {/* <Link
                                data-test-selector="brandToProductPageLink"
                                href={brand.productListPagePath}
                                {...styles.productLink}
                            >
                                {translate("Shop All {0} Products").replace("{0}", brand.name)}
                            </Link> */}
                        {/* </GridItem> */}
                        {brand.externalUrl && (
                            // <GridItem {...styles.externalLinkItem}>
                            <Link
                                data-test-selector="brandExternalUrlLink"
                                href={brand.externalUrl}
                                {...styles.externalLink}
                            >
                                {translate("Visit {0} Website").replace("{0}", brand.name)}
                            </Link>
                            // </GridItem>
                        )}
                    </StyledWrapper>
                    {/* </GridContainer> */}
                </GridItem>
            )}
        </GridContainer>
    );
};

interface BrandDetailsLinkProps {
    path?: string;
    gridItemStyles?: GridItemProps;
    linkStyles?: LinkPresentationProps;
}

const BrandLink: FC<PropsWithChildren<BrandDetailsLinkProps>> = ({ path, gridItemStyles, linkStyles, children }) => {
    if (!path) {
        return null;
    }
    return (
        <GridItem {...gridItemStyles}>
            <Link data-test-selector="brandToProductPageLink" href={path} {...linkStyles}>
                {children}
            </Link>
        </GridItem>
    );
};

const widgetModule: WidgetModule = {
    component: BrandDetailsImage,
    definition: {
        group: "Brand Details",
        icon: "image",
        // XNG-Changes
        // displayName: "Brand Image",
        displayName: "Brand Image And Links",
        allowedContexts: ["BrandDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showImage,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showLinks,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
